import {
  AnalyticsEvent,
  ButtonComponentV2,
  DeleteExternalCardInput,
  LendingPaymentSource,
  Mutation,
  Typenames,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { TabaPayEntryFlowType } from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import { useTabaPayAddress } from 'features/moveMoney/screens/dcf/tabapay/providers/TabaPayContextProvider';
import { getTabapayErrorScreen } from 'features/myVaro/screens/ExternalAccounts/helper/TabapayHelper';
import { useEffect, useState } from 'react';
import DeleteExternalCard from 'services/graphql/mutations/DeleteExternalCard';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { isCombinedError } from 'shared/utils/errors/combinedErrorUtils';
import { isCoreError } from 'shared/utils/errors/CoreError';
import { useMutation } from 'urql';

type UseDeleteExternalCardProps = {
  shouldNavigateToExternalAccounts?: boolean;
};

const useDeleteExternalCard = ({
  shouldNavigateToExternalAccounts = false,
}: UseDeleteExternalCardProps = {}) => {
  const [result, executeMutation] = useMutation<Mutation>(DeleteExternalCard);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>();
  const [errorModel, setErrorModel] = useState<{
    screenType: ErrorScreenType.Error;
    messageHeadline: string;
    messageBody: string;
    primaryButton?: ButtonComponentV2;
    secondaryButton?: ButtonComponentV2;
    is3DSError?: boolean;
    analytics?: AnalyticsEvent;
  } | null>(null);
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();
  const router = useRouter();

  const responseError = useFormattedCombinedError(result.error);
  const { setShouldRefreshCards } = useTabaPayAddress();
  const [savedNavigationParams, setSavedNavigationParams] = useState<{
    tabaPayEntryFlowType?: TabaPayEntryFlowType;
    dueDate?: string;
    feeAmount?: string;
    amountId?: string;
    advanceAmount?: string;
    repaymentDate?: string;
    selectedAccountId?: string;
    paymentSource?: LendingPaymentSource;
    selectedAccountName?: string;
  }>();
  const { refreshTiles, data, fetching, error } = useRefreshHomeTiles([
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHECKING,
    HomeTileTypeIds.ACCOUNT_ACTION_TILE_ADD_MONEY_DDA,
  ] as string[]);

  useEffect(() => {
    if ((data?.tiles || error) && !fetching) {
      setIsLoading(false);
      if (shouldNavigateToExternalAccounts) {
        router.dismissTo({
          pathname: '/myvaro/external-accounts',
          params: savedNavigationParams || {},
        });
      }
    }
  }, [data, error, fetching, shouldNavigateToExternalAccounts, router]);

  useEffect(() => {
    if (responseError?.message && responseError.title) {
      setErrorModel({
        screenType: ErrorScreenType.Error,
        messageHeadline: responseError.title,
        messageBody: responseError.message,
      });
    }
  }, [responseError?.message, responseError?.title]);

  const deleteExternalCard = async (
    input: DeleteExternalCardInput,
    navigationParams?: {
      tabaPayEntryFlowType?: TabaPayEntryFlowType;
      dueDate?: string;
      feeAmount?: string;
      amountId?: string;
      advanceAmount?: string;
      repaymentDate?: string;
      selectedAccountId?: string;
      paymentSource?: LendingPaymentSource;
      selectedAccountName?: string;
    },
  ) => {
    try {
      setSavedNavigationParams(navigationParams);
      setErrorModel(null);
      setIsLoading(true);
      const result = await executeMutation({ input: input });
      const errorScreen = getTabapayErrorScreen(
        result?.data?.deleteExternalCard,
      );
      if (errorScreen) {
        setErrorModel(errorScreen);
        setIsLoading(false);
      }
      if (
        result?.data?.deleteExternalCard?.__typename ===
        Typenames.DeleteExternalCardResultError
      ) {
        setErrorModel({
          screenType: ErrorScreenType.Error,
          messageHeadline: result.data.deleteExternalCard.title,
          messageBody: result.data.deleteExternalCard.message,
        });
        setIsLoading(false);
      }
      if (
        result?.data?.deleteExternalCard?.__typename ===
        Typenames.DeleteExternalCardResultSuccess
      ) {
        refreshTiles();
        setIsSuccess(result.data.deleteExternalCard.success);
        setShouldRefreshCards(true);
      }
    } catch (error) {
      setIsLoading(false);
      let errorTitle: string = defaultTitle;
      let errorMessage: string = defaultMessage;

      if (isCoreError(error)) {
        errorTitle = error.title;
        errorMessage = error.message;
      } else if (isCombinedError(error)) {
        errorMessage = error.message;
      }

      setErrorModel({
        screenType: ErrorScreenType.Error,
        messageHeadline: errorTitle,
        messageBody: errorMessage,
      });
    }
  };
  return {
    deleteExternalCard,
    isSuccess,
    isLoading,
    errorModel,
  };
};

export default useDeleteExternalCard;
